/**
 * --- External Imports ---
 */
import { ComponentType, FC, useMemo } from "react";

/**
 * --- Internal Imports ---
 */
import { IFormField } from "../../../../api/dtos/dtos";
import { IFormFieldComponentProps } from "../types/FieldTypes";
import FormFieldCheckbox from "./FormFieldCheckbox";
import FormFieldCheckboxGroup from "./FormFieldCheckboxGroup";
import FormFieldDate from "./FormFieldDate";
import FormFieldRadioGroup from "./FormFieldRadioGroup";
import FormFieldTextField from "./FormFieldTextField";
import FormFieldCustom from "./FormFieldCustom";
import { Condition, ConditionMode, useFormState } from "@ngt/forms-core";
import FormFieldDropdown from "./FormFieldDropdown";
import FormFieldTime from "./FormFieldTime";
import FormFieldGoogleAutocomplete from "./FormFieldGoogleAutocomplete";
import FormFieldDropdownWithNumberField from "./FormFieldDropdownWithNumberField";

/**
 * --- Constants ---
 */
export const getFieldType = (field: IFormField): string | null => {
    return (field as any)?.__type?.split(',')?.[0]?.split('.')?.pop() ?? null;
}

const FormFieldComponentMapping: Record<string, ComponentType<IFormFieldComponentProps<any>>> = {
    ["TextFieldFormField"]: FormFieldTextField,
    ["DateFormField"]: FormFieldDate,
    ["TimeFormField"]: FormFieldTime,
    ["RadioGroupFormField"]: FormFieldRadioGroup,
    ["CheckboxGroupFormField"]: FormFieldCheckboxGroup,
    ["CheckboxFormField"]: FormFieldCheckbox,
    ["CustomFormField"]: FormFieldCustom,
    ["DropdownFormField"]: FormFieldDropdown,
    ["GoogleAutocompleteFormField"]: FormFieldGoogleAutocomplete,
    ["AutoSuggestFormField"]: FormFieldTextField,
    ["DropdownWithNumberFormField"]: FormFieldDropdownWithNumberField,
}

/**
 * --- Default / Unknown Field Component ---
 */
const FormFieldUnknown: FC<IFormFieldComponentProps> = () => {
    return (
        <>
            Unknown Field Type
        </>
    );
};

/**
 * --- Form Field Component ---
 * 
 * This represents the base component used to map each "IFormField" form 
 * a species form into its appropriate rendered object.
 */
const FormFieldComponent: FC<IFormFieldComponentProps> = ({
    field,
    form,
    conditionFn
}) => {
    const { value } = useFormState({ value: true });
    
    const type = useMemo(() => {
        return getFieldType(field) ?? 'UNDEFINED|TYPE';
    }, [field]);

    const Component = FormFieldComponentMapping[type] ?? FormFieldUnknown;

    if (conditionFn) {
        return (
            <Condition
                condition={conditionFn}
                subscription={{ value }}
                mode={ConditionMode.Show}
            >
                <Component field={field} form={form} />
            </Condition>
        )
    }
    
    return (
        <Component field={field} form={form} />
    );
}

export default FormFieldComponent;